if (!customElements.get('size-guide-modal')) {
  customElements.define(
    'size-guide-modal',
    class extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.closeButton = this.querySelector('.close-button');

        // Header container for switching between multiple size guides
        this.guideHeader = document.createElement('div');
        this.guideHeader.classList.add('guide-header');
        this.dialog.prepend(this.guideHeader);

        // Parse embedded JSON data containing one or more guide objects
        const jsonScript = document.getElementById('size-guide-data');
        if (jsonScript) {
          try {
            /**
             * Expected JSON structure:
             * [
             *   { type: "Shoe", html: "<table>...</table>" },
             *   { type: "Jacket", html: "<table>...</table>" }
             * ]
             */
            const guides = JSON.parse(jsonScript.textContent);
            this.setGuides(guides);
          } catch (e) {
            console.error('Invalid size guide JSON:', e);
          }
        }

        this.addEventListeners();
      }

      addEventListeners() {
        // External button that opens the modal
        const openBtn = document.getElementById('open-size-guide');
        if (openBtn) {
          openBtn.addEventListener('click', () => this.dialog?.showModal());
        }

        if (this.closeButton) {
          this.closeButton.addEventListener('click', () => {
            if (this.dialog) this.dialog.close();
          });
        }

        // Prevent clicks on internal UI from closing dialog
        this.dialog.querySelectorAll('.guide-header, .tab-header').forEach((el) => {
          el.addEventListener('click', (e) => e.stopPropagation());
        });

        // Handle clicks outside dialog to close it (for buggy browser backdrops)
        this.dialog.addEventListener('click', (event) => {
          const rect = this.dialog.getBoundingClientRect();
          const clickOutside =
            event.clientX < rect.left ||
            event.clientX > rect.right ||
            event.clientY < rect.top ||
            event.clientY > rect.bottom;
          if (clickOutside) this.dialog.close();
        });
      }

      /**
       * Build top-level guide tabs (e.g., “Shoes”, “Jackets”)
       * @param {Array<{type: string, html: string}>} guides
       */
      setGuides(guides) {
        this.guideHeader.innerHTML = '';
        this.dialog.querySelectorAll('.guide-content').forEach((el) => el.remove());

        const multipleGuides = guides.length > 1;

        guides.forEach((guide, index) => {
          const { guideName, html } = guide;

          // Wrapper for each guide's content
          const wrapper = document.createElement('div');
          wrapper.classList.add('guide-content');
          if (index === 0) wrapper.classList.add('active');
          this.dialog.appendChild(wrapper);

          // Inject HTML content + internal tabs
          this.setContent(html, wrapper);

          if (multipleGuides) {
            // Create a button using the guide type name (fallback if missing)
            const guideButton = document.createElement('button');
            guideButton.textContent = guideName || `Size Guide ${index + 1}`;
            guideButton.dataset.guide = index;
            if (index === 0) guideButton.classList.add('active');

            guideButton.addEventListener('click', () => {
              // Toggle active states for guide tabs
              this.guideHeader.querySelectorAll('button').forEach((b) => b.classList.remove('active'));
              this.dialog.querySelectorAll('.guide-content').forEach((g) => g.classList.remove('active'));

              guideButton.classList.add('active');
              wrapper.classList.add('active');
            });

            this.guideHeader.appendChild(guideButton);
          }
        });
      }

      /**
       * Build per-guide tab UI (one tab per <table>)
       */
      setContent(htmlString, container) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');
        const tables = doc.querySelectorAll('table');

        const tabHeader = document.createElement('div');
        tabHeader.classList.add('tab-header');
        container.appendChild(tabHeader);

        tables.forEach((table, i) => {
          const tableClone = table.cloneNode(true);
          tableClone.classList.add('size-guide-table');
          tableClone.querySelector('colgroup')?.remove();

          const rows = tableClone.querySelectorAll('tr');
          if (rows.length === 0) return;

          // First row defines tab label
          const firstRow = rows[0];
          const firstCell = firstRow.querySelector('td, th');
          const tabLabel = firstCell ? firstCell.textContent.trim() : `Tab ${i + 1}`;
          firstRow.remove();

          // Optional image row (if any)
          let imageElement = null;
          const secondRow = tableClone.querySelectorAll('tr')[0];
          if (secondRow && secondRow.querySelector('img')) {
            imageElement = secondRow.querySelector('img').cloneNode(true);
            secondRow.remove();
          }

          // Create tab button
          const tabButton = document.createElement('button');
          tabButton.textContent = tabLabel;
          tabButton.dataset.tab = i;
          if (i === 0) tabButton.classList.add('active');
          tabHeader.appendChild(tabButton);

          // Create tab content container
          const contentDiv = document.createElement('div');
          contentDiv.classList.add('tab-content');
          if (i === 0) contentDiv.classList.add('active');

          if (imageElement) {
            const imgWrapper = document.createElement('div');
            imgWrapper.classList.add('size-guide-image');
            imgWrapper.appendChild(imageElement);
            contentDiv.appendChild(imgWrapper);
          }

          contentDiv.appendChild(tableClone);
          container.appendChild(contentDiv);

          // Tab switching logic within each guide
          tabButton.addEventListener('click', () => {
            tabHeader.querySelectorAll('button').forEach((b) => b.classList.remove('active'));
            container.querySelectorAll('.tab-content').forEach((c) => c.classList.remove('active'));
            tabButton.classList.add('active');
            contentDiv.classList.add('active');
          });
        });
      }
    }
  );
}
